import React, { useEffect, useState } from 'react';
import {
  ArrowLeft,
  FileText,
  Video,
  ExternalLink,
  Users,
  Clock,
  Calendar,
  MapPin,
  Link as LinkIcon,
  Eye,
  Edit,
  Trash2,
} from 'lucide-react';
import './LessonDetails.css';
import { LoadingSpinner, ErrorDisplay } from '@/Components/common';
import useSuperAdminContentManagementStore from '@/stores/SuperAdminStore/SuperAdminContentManagementStore';
import LessonViewer from '../LessonViewer/LessonViewer';

interface LessonDetailsProps {
  lessonId: number;
  onClose: () => void;
  onEdit?: (lessonId: number) => void;
  onDelete?: (lessonId: number) => void;
}

interface DeleteConfirmProps {
  onConfirm: () => void;
  onCancel: () => void;
}

const DeleteConfirmation: React.FC<DeleteConfirmProps> = ({ onConfirm, onCancel }) => (
  <div className="s-admin-lesson-details__delete-overlay">
    <div className="s-admin-lesson-details__delete-modal">
      <h3>Delete Lesson?</h3>
      <p>Are you sure you want to delete this lesson? This action cannot be undone.</p>
      <div className="s-admin-lesson-details__delete-actions">
        <button
          className="s-admin-lesson-details__btn s-admin-lesson-details__btn--confirm-delete"
          onClick={onConfirm}
        >
          Yes, Delete
        </button>
        <button
          className="s-admin-lesson-details__btn s-admin-lesson-details__btn--cancel-delete"
          onClick={onCancel}
        >
          Cancel
        </button>
      </div>
    </div>
  </div>
);

export default function LessonDetails({
  lessonId,
  onClose,
  onEdit,
  onDelete,
}: LessonDetailsProps) {
  const [showContentViewer, setShowContentViewer] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const {
    selectedLesson: lesson,
    loading,
    error,
    fetchStandaloneLessonById,
  } = useSuperAdminContentManagementStore();

  useEffect(() => {
    if (lessonId) {
      console.log('LessonDetails - Fetching lesson:', lessonId);
      fetchStandaloneLessonById(lessonId);
    }
  }, [lessonId, fetchStandaloneLessonById]);

  const handleDeleteClick = () => {
    setShowDeleteConfirm(true);
  };

  const handleConfirmDelete = () => {
    if (onDelete && lesson) {
      onDelete(lesson.id);
      setShowDeleteConfirm(false);
      onClose();
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteConfirm(false);
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const formatTime = (timeString: string) => {
    if (!timeString) return 'N/A';
    return timeString;
  };

  const getContentTypeIcon = (contentType?: string | null) => {
    switch (contentType) {
      case 'mp4':
        return <Video size={20} />;
      case 'document':
        return <FileText size={20} />;
      case 'scorm':
        return <FileText size={20} />;
      case 'url':
        return <ExternalLink size={20} />;
      default:
        return <FileText size={20} />;
    }
  };

  if (showContentViewer && lesson) {
    return (
      <LessonViewer
        lessonId={lesson.id}
        lessonData={lesson}
        onClose={() => setShowContentViewer(false)}
      />
    );
  }

  if (loading) {
    return (
      <div className="s-admin-lesson-details">
        <LoadingSpinner fullScreen size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="s-admin-lesson-details">
        <ErrorDisplay
          fullScreen
          title="Failed to Load Lesson"
          message={error}
          type="error"
          showRetry
          onRetry={() => fetchStandaloneLessonById(lessonId)}
        />
      </div>
    );
  }

  if (!lesson) {
    return (
      <div className="s-admin-lesson-details">
        <ErrorDisplay
          fullScreen
          title="Lesson Not Found"
          message="The lesson you're looking for doesn't exist."
          type="error"
        />
      </div>
    );
  }

  return (
    <div className="s-admin-lesson-details">
      {/* Header */}
      <div className="s-admin-lesson-details__header">
        <button onClick={onClose} className="s-admin-lesson-details__back-btn">
          <ArrowLeft size={20} />
          Back to Library
        </button>
        <div className="s-admin-lesson-details__breadcrumb">
          <span>Content Management</span>
          <span className="s-admin-lesson-details__breadcrumb-separator">›</span>
          <span>Library</span>
          <span className="s-admin-lesson-details__breadcrumb-separator">›</span>
          <span className="s-admin-lesson-details__breadcrumb-current">{lesson.title}</span>
        </div>
      </div>

      {/* Main Content */}
      <div className="s-admin-lesson-details__content">
        {/* Title Section */}
        <div className="s-admin-lesson-details__title-section">
          <h1 className="s-admin-lesson-details__title">{lesson.title}</h1>
          <div className="s-admin-lesson-details__badges">
            <span className="s-admin-lesson-details__badge s-admin-lesson-details__badge--type">
              {lesson.lessonType === 'ILTS' ? (
                <>
                  <Users size={16} />
                  {lesson.lessonType}
                </>
              ) : (
                <>
                  {getContentTypeIcon(lesson.contentType)}
                  {lesson.contentType?.toUpperCase() || 'Content-Based'}
                </>
              )}
            </span>
            {lesson.lessonDuration && (
              <span className="s-admin-lesson-details__badge s-admin-lesson-details__badge--duration">
                <Clock size={16} />
                {lesson.lessonDuration}
              </span>
            )}
          </div>
        </div>

        {/* Description */}
        {lesson.description && (
          <div className="s-admin-lesson-details__section">
            <h2 className="s-admin-lesson-details__section-title">Description</h2>
            <p className="s-admin-lesson-details__description">{lesson.description}</p>
          </div>
        )}

        {/* ILTS Information */}
        {lesson.lessonType === 'ILTS' && lesson.iltsInfo && (
          <div className="s-admin-lesson-details__section">
            <h2 className="s-admin-lesson-details__section-title">Session Information</h2>
            <div className="s-admin-lesson-details__ilts-grid">
              <div className="s-admin-lesson-details__info-card">
                <div className="s-admin-lesson-details__info-icon">
                  <Users size={20} />
                </div>
                <div className="s-admin-lesson-details__info-content">
                  <label>Session Mode</label>
                  <p>{lesson.iltsInfo.mode}</p>
                </div>
              </div>

              {lesson.iltsInfo.startDate && (
                <div className="s-admin-lesson-details__info-card">
                  <div className="s-admin-lesson-details__info-icon">
                    <Calendar size={20} />
                  </div>
                  <div className="s-admin-lesson-details__info-content">
                    <label>Start Date</label>
                    <p>{formatDate(lesson.iltsInfo.startDate)}</p>
                  </div>
                </div>
              )}

              {lesson.iltsInfo.startTime && (
                <div className="s-admin-lesson-details__info-card">
                  <div className="s-admin-lesson-details__info-icon">
                    <Clock size={20} />
                  </div>
                  <div className="s-admin-lesson-details__info-content">
                    <label>Start Time</label>
                    <p>{formatTime(lesson.iltsInfo.startTime)}</p>
                  </div>
                </div>
              )}

              {lesson.iltsInfo.endDate && (
                <div className="s-admin-lesson-details__info-card">
                  <div className="s-admin-lesson-details__info-icon">
                    <Calendar size={20} />
                  </div>
                  <div className="s-admin-lesson-details__info-content">
                    <label>End Date</label>
                    <p>{formatDate(lesson.iltsInfo.endDate)}</p>
                  </div>
                </div>
              )}

              {lesson.iltsInfo.endTime && (
                <div className="s-admin-lesson-details__info-card">
                  <div className="s-admin-lesson-details__info-icon">
                    <Clock size={20} />
                  </div>
                  <div className="s-admin-lesson-details__info-content">
                    <label>End Time</label>
                    <p>{formatTime(lesson.iltsInfo.endTime)}</p>
                  </div>
                </div>
              )}

              {lesson.iltsInfo.meetUrl && (
                <div className="s-admin-lesson-details__info-card lesson-details__info-card--full">
                  <div className="s-admin-lesson-details__info-icon">
                    <LinkIcon size={20} />
                  </div>
                  <div className="s-admin-lesson-details__info-content">
                    <label>Meeting URL</label>
                    <a
                      href={lesson.iltsInfo.meetUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="s-admin-lesson-details__link"
                    >
                      {lesson.iltsInfo.meetUrl}
                    </a>
                  </div>
                </div>
              )}

              {lesson.iltsInfo.venue && (
                <div className="s-admin-lesson-details__info-card s-admin-lesson-details__info-card--full">
                  <div className="s-admin-lesson-details__info-icon">
                    <MapPin size={20} />
                  </div>
                  <div className="s-admin-lesson-details__info-content">
                    <label>Venue</label>
                    <p>{lesson.iltsInfo.venue}</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Content Information */}
        {lesson.lessonType === 'Content-Based' && lesson.contentType && (
          <div className="s-admin-lesson-details__section">
            <h2 className="s-admin-lesson-details__section-title">Content Information</h2>
            <div className="s-admin-lesson-details__content-info">
              <div className="s-admin-lesson-details__info-card">
                <div className="s-admin-lesson-details__info-icon">
                  {getContentTypeIcon(lesson.contentType)}
                </div>
                <div className="s-admin-lesson-details__info-content">
                  <label>Content Type</label>
                  <p>{lesson.contentType.toUpperCase()}</p>
                </div>
              </div>

              {lesson.lessonContentUrl && lesson.contentType === 'url' && (
                <div className="s-admin-lesson-details__info-card s-admin-lesson-details__info-card--full">
                  <div className="s-admin-lesson-details__info-icon">
                    <ExternalLink size={20} />
                  </div>
                  <div className="s-admin-lesson-details__info-content">
                    <label>URL</label>
                    <a
                      href={lesson.lessonContentUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="s-admin-lesson-details__link"
                    >
                      {lesson.lessonContentUrl}
                    </a>
                  </div>
                </div>
              )}

              {lesson.lessonContentDocument && lesson.contentType === 'document' && (
                <div className="s-admin-lesson-details__info-card">
                  <div className="s-admin-lesson-details__info-icon">
                    <FileText size={20} />
                  </div>
                  <div className="s-admin-lesson-details__info-content">
                    <label>Document File</label>
                    <p>{lesson.lessonContentDocument}</p>
                  </div>
                </div>
              )}

              {lesson.lessonContentMp4 && lesson.contentType === 'mp4' && (
                <div className="s-admin-lesson-details__info-card">
                  <div className="s-admin-lesson-details__info-icon">
                    <Video size={20} />
                  </div>
                  <div className="s-admin-lesson-details__info-content">
                    <label>Video File</label>
                    <p>{lesson.lessonContentMp4}</p>
                  </div>
                </div>
              )}

              {lesson.lessonContentScorm && lesson.contentType === 'scorm' && (
                <div className="s-admin-lesson-details__info-card">
                  <div className="s-admin-lesson-details__info-icon">
                    <FileText size={20} />
                  </div>
                  <div className="s-admin-lesson-details__info-content">
                    <label>SCORM Package</label>
                    <p>{lesson.lessonContentScorm}</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Metadata */}
        <div className="s-admin-lesson-details__section">
          <h2 className="s-admin-lesson-details__section-title">Metadata</h2>
          <div className="s-admin-lesson-details__metadata">
            <div className="s-admin-lesson-details__info-card">
              <div className="s-admin-lesson-details__info-content">
                <label>Created Date</label>
                <p>{formatDate(lesson.createdDate)}</p>
              </div>
            </div>
            <div className="s-admin-lesson-details__info-card">
              <div className="s-admin-lesson-details__info-content">
                <label>Last Updated</label>
                <p>{formatDate(lesson.lastUpdated)}</p>
              </div>
            </div>
            <div className="s-admin-lesson-details__info-card">
              <div className="s-admin-lesson-details__info-content">
                <label>Created By</label>
                <p>{lesson.creatorName || 'Unknown'}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="s-admin-lesson-details__actions">
          <button
            className="s-admin-lesson-details__btn s-admin-lesson-details__btn--view"
            onClick={() => setShowContentViewer(true)}
          >
            <Eye size={20} />
            View Content
          </button>
          {onEdit && (
            <button
              className="s-admin-lesson-details__btn s-admin-lesson-details__btn--edit"
              onClick={() => onEdit(lesson.id)}
            >
              <Edit size={20} />
              Edit Lesson
            </button>
          )}
          {onDelete && (
            <button
              className="s-admin-lesson-details__btn s-admin-lesson-details__btn--delete"
              onClick={handleDeleteClick}
            >
              <Trash2 size={20} />
              Delete Lesson
            </button>
          )}
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <DeleteConfirmation
          onConfirm={handleConfirmDelete}
          onCancel={handleCancelDelete}
        />
      )}
    </div>
  );
}
